import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { SEO, Breadcrumb } from '@/components/seo';
import { FAQSchema } from '@/components/seo/Schema';
import { ArrowRight, Loader2, CheckCircle, Cpu, Shield, Zap, ChevronDown, Droplet } from 'lucide-react';
import { Dropzone } from '@/components/dropzone/Dropzone';
import { buttonStyles } from '@/components/ui';
import { Stats, Testimonials, BeforeAfter } from '@/components/sections';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface FAQItem {
  q: string;
  a: string;
}

interface WatermarkData {
  id: string;
  slug: string;
  name: string;
  developer: string | null;
  description: string | null;
  longDescription: string | null;
  faq: FAQItem[] | null;
  type: string | null;
  technology: string | null;
  isRemovable: boolean;
  removalMethod: string | null;
  logoUrl: string | null;
}

interface ModelData {
  slug: string;
  name: string;
  vendor: string;
  avgDetectionRate: number | null;
  isDefault: boolean;
  logoUrl: string | null;
}

interface ServiceData {
  slug: string;
  name: string;
  vendor: string;
  logoUrl: string | null;
}

interface DetectionStat {
  slug: string;
  name: string;
  total: number;
  correct: number;
  successRate: number;
  avgScore: number | null;
}

interface RelatedWatermark {
  slug: string;
  name: string;
  developer: string | null;
  type: string | null;
  isRemovable: boolean;
  logoUrl: string | null;
}

export function WatermarkPage() {
  const { slug } = useParams<{ slug: string }>();
  const [watermark, setWatermark] = useState<WatermarkData | null>(null);
  const [models, setModels] = useState<ModelData[]>([]);
  const [services, setServices] = useState<ServiceData[]>([]);
  const [detectionStats, setDetectionStats] = useState<DetectionStat[]>([]);
  const [relatedWatermarks, setRelatedWatermarks] = useState<RelatedWatermark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/pages/watermarks/${slug}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load watermark');
        }

        setWatermark(data.watermark);
        setModels(data.models || []);
        setServices(data.services || []);
        setDetectionStats(data.detectionStats || []);
        setRelatedWatermarks(data.relatedWatermarks || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error || !watermark) {
    return <Navigate to="/watermarks" replace />;
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Watermarks', href: '/watermarks' },
    { label: watermark.name },
  ];

  return (
    <>
      <SEO
        title={`Remove ${watermark.name} Watermark - Free AI Watermark Remover`}
        description={watermark.description || `Remove ${watermark.name} watermarks from AI-generated images. Free online tool by ${watermark.developer}.`}
        canonical={`/watermarks/${watermark.slug}`}
      />

      {/* FAQ Schema for rich snippets */}
      {watermark.faq && watermark.faq.length > 0 && (
        <FAQSchema
          questions={watermark.faq.map(item => ({
            question: item.q,
            answer: item.a,
          }))}
        />
      )}

      {/* Hero */}
      <section className="section bg-gradient-to-b from-purple-50 via-white to-white">
        <div className="container">
          <Breadcrumb items={breadcrumbItems} />

          <div className="max-w-4xl mx-auto text-center mt-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Remove {watermark.name} from AI Images
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {watermark.description}
            </p>
          </div>

          {/* Dropzone */}
          <div className="max-w-2xl mx-auto" id="tool">
            <Dropzone onFileSelect={() => {
              window.location.href = `/undetectable?watermark=${watermark.slug}`;
            }} />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              How We Remove {watermark.name}
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-purple-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Detection</h3>
                <p className="text-gray-600 text-sm">
                  We analyze the image to detect {watermark.technology || 'watermark'} patterns
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Removal</h3>
                <p className="text-gray-600 text-sm">
                  {watermark.removalMethod || 'Advanced algorithms remove the watermark'}
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Quality</h3>
                <p className="text-gray-600 text-sm">
                  Image quality preserved with lossless processing
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              About {watermark.name}
            </h2>

            <div className="bg-white rounded-xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500 text-sm">Developer</span>
                  <p className="font-medium text-gray-900">{watermark.developer || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">Type</span>
                  <p className="font-medium text-gray-900 capitalize">{watermark.type || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">Technology</span>
                  <p className="font-medium text-gray-900 capitalize">{watermark.technology || 'Proprietary'}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">Removable</span>
                  <p className="font-medium text-gray-900">{watermark.isRemovable ? 'Yes' : 'Partially'}</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Models using this watermark */}
      {models.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Cpu className="w-6 h-6 text-purple-500" />
                AI Models Using {watermark.name}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {models.map((model) => (
                  <Link
                    key={model.slug}
                    to={`/models/${model.slug}`}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors flex items-center gap-4"
                  >
                    {model.logoUrl ? (
                      <img src={model.logoUrl} alt={model.name} className="w-10 h-10 object-contain" />
                    ) : (
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{model.name}</h3>
                      <p className="text-sm text-gray-500">{model.vendor}</p>
                    </div>
                    {model.isDefault && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                        Default
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Services using this watermark */}
      {services.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Services Using {watermark.name}
              </h2>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {services.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/services/${service.slug}`}
                    className="bg-white rounded-xl p-4 hover:shadow-md transition-shadow flex items-center gap-3"
                  >
                    {service.logoUrl ? (
                      <img src={service.logoUrl} alt={service.name} className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 text-sm font-medium">
                        {service.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">{service.name}</h3>
                      <p className="text-sm text-gray-500">{service.vendor}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Detection Stats */}
      {detectionStats.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-500" />
                Detection by Detector
              </h2>

              <div className="space-y-3">
                {detectionStats.map((stat) => (
                  <Link
                    key={stat.slug}
                    to={`/detectors/${stat.slug}`}
                    className="flex items-center justify-between bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{stat.name}</span>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            stat.successRate >= 70 ? 'bg-green-500' :
                            stat.successRate >= 40 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${stat.successRate}%` }}
                        />
                      </div>
                      <span className="text-sm w-12 text-right">
                        {stat.successRate}%
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      <Stats />

      {/* Before/After */}
      <BeforeAfter />

      {/* SEO Text */}
      {watermark.longDescription && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-3xl mx-auto prose prose-gray">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {watermark.longDescription}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {watermark.faq && watermark.faq.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {watermark.faq.map((item, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <span className="font-medium text-gray-900">{item.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-5 text-gray-600">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related Watermarks */}
      {relatedWatermarks.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Droplet className="w-6 h-6 text-purple-500" />
                Other Watermark Types
              </h2>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {relatedWatermarks.map((rw) => (
                  <Link
                    key={rw.slug}
                    to={`/watermarks/${rw.slug}`}
                    className="bg-white rounded-xl p-4 hover:shadow-md transition-shadow flex items-start gap-3"
                  >
                    {rw.logoUrl && (
                      <img src={rw.logoUrl} alt={rw.name} className="w-8 h-8 object-contain flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">{rw.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">{rw.developer}</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          rw.isRemovable ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {rw.isRemovable ? 'Removable' : 'Partial'}
                        </span>
                        {rw.type && (
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full capitalize">
                            {rw.type}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <Testimonials />

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Remove {watermark.name} Now
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Free, fast, and secure. No sign-up required.
          </p>
          <a
            href="#tool"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-primary-600 hover:bg-gray-100',
            })}
          >
            Remove Watermark Free
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </>
  );
}

export default WatermarkPage;
